import axios from 'axios';

const API_BASE = '/api/c2';

// Create axios instance with auth headers
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const c2Service = {
  // ============= BEACONS =============

  getBeacons: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.status) params.append('status', filters.status);
    if (filters.os) params.append('os', filters.os);
    if (filters.isAlive !== undefined) params.append('isAlive', filters.isAlive);

    return await apiClient.get(`/beacons?${params.toString()}`);
  },

  getBeacon: async (id) => {
    return await apiClient.get(`/beacons/${id}`);
  },

  approveBeacon: async (id, projectId) => {
    return await apiClient.post(`/beacons/${id}/approve`, { projectId });
  },

  updateBeacon: async (id, updates) => {
    return await apiClient.put(`/beacons/${id}`, updates);
  },

  killBeacon: async (id) => {
    return await apiClient.delete(`/beacons/${id}/kill`);
  },

  deleteBeacon: async (id) => {
    return await apiClient.delete(`/beacons/${id}`);
  },

  // ============= TASKS =============

  getTasks: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.status) params.append('status', filters.status);
    if (filters.command) params.append('command', filters.command);
    if (filters.beaconId) params.append('beaconId', filters.beaconId);

    return await apiClient.get(`/tasks?${params.toString()}`);
  },

  getAllTasks: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.limit) params.append('limit', filters.limit);

    return await apiClient.get(`/tasks?${params.toString()}`);
  },

  getTask: async (id) => {
    return await apiClient.get(`/tasks/${id}`);
  },

  createTask: async (beaconId, command, args) => {
    return await apiClient.post(`/beacons/${beaconId}/tasks`, {
      command,
      args
    });
  },

  approveTask: async (id) => {
    return await apiClient.post(`/tasks/${id}/approve`);
  },

  cancelTask: async (id) => {
    return await apiClient.post(`/tasks/${id}/cancel`);
  },

  retryTask: async (id) => {
    return await apiClient.post(`/tasks/${id}/retry`);
  },

  bulkCreateActions: async (taskIds) => {
    return await apiClient.post(`/tasks/bulk-create-actions`, { taskIds });
  },

  // ============= CREDENTIALS =============

  getCredentials: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.username) params.append('username', filters.username);
    if (filters.domain) params.append('domain', filters.domain);

    return await apiClient.get(`/credentials?${params.toString()}`);
  },

  getCredential: async (id) => {
    return await apiClient.get(`/credentials/${id}`);
  },

  decryptCredential: async (id) => {
    return await apiClient.get(`/credentials/${id}/decrypt`);
  },

  exportCredential: async (id, format) => {
    return await apiClient.post(`/credentials/${id}/export`, { format });
  },

  deleteCredential: async (id) => {
    return await apiClient.delete(`/credentials/${id}`);
  },

  // ============= FILES =============

  getFiles: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.beaconId) params.append('beaconId', filters.beaconId);
    if (filters.filename) params.append('filename', filters.filename);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.page) params.append('page', filters.page);
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.order) params.append('order', filters.order);

    return await apiClient.get(`/files?${params.toString()}`);
  },

  getFile: async (id) => {
    return await apiClient.get(`/files/${id}`);
  },

  getFilePreview: async (id) => {
    return await apiClient.get(`/files/${id}/preview`);
  },

  getFileThumbnail: (id, size = 'medium') => {
    const token = localStorage.getItem('token');
    return `/api/c2/files/${id}/thumbnail?size=${size}&token=${token}`;
  },

  downloadFile: async (id, filename) => {
    const response = await apiClient.get(`/files/${id}/download`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'download');
    document.body.appendChild(link);
    link.click();
    link.remove();

    return response;
  },

  deleteFile: async (id) => {
    return await apiClient.delete(`/files/${id}`);
  },

  // ============= LISTENERS =============

  getListeners: async () => {
    return await apiClient.get('/listeners');
  },

  getListener: async (id) => {
    return await apiClient.get(`/listeners/${id}`);
  },

  createListener: async (listenerData) => {
    return await apiClient.post('/listeners', listenerData);
  },

  startListener: async (id) => {
    return await apiClient.put(`/listeners/${id}/start`);
  },

  stopListener: async (id) => {
    return await apiClient.put(`/listeners/${id}/stop`);
  },

  updateListener: async (id, updates) => {
    return await apiClient.put(`/listeners/${id}`, updates);
  },

  getListenerStatus: async (id) => {
    return await apiClient.get(`/listeners/${id}/status`);
  },

  deleteListener: async (id) => {
    return await apiClient.delete(`/listeners/${id}`);
  },

  // ============= PAYLOADS (Planned) =============

  generatePayload: async (config) => {
    return await apiClient.post('/payloads/generate', config);
  },

  downloadPayload: async (id) => {
    return await apiClient.get(`/payloads/${id}/download`, {
      responseType: 'blob'
    });
  },

  // ============= STAGED PAYLOADS =============

  getStageInfo: async () => {
    return await apiClient.get('/stage/info');
  },

  uploadStagePayload: async (platform, arch, variant, file) => {
    const formData = new FormData();
    formData.append('payload', file);
    formData.append('platform', platform);
    formData.append('arch', arch);
    formData.append('variant', variant);

    return await apiClient.post('/stage/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  downloadStagePayload: async (platform, arch, variant, filename) => {
    const params = new URLSearchParams({ platform, arch, variant });
    const response = await apiClient.get(`/stage/download?${params.toString()}`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const ext = platform === 'windows' ? '.exe' : platform === 'darwin' ? '' : '.elf';
    link.setAttribute('download', filename || `agent-${platform}-${arch}-${variant}${ext}`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return response;
  },

  buildPayloads: async (platform = 'all', variant = 'all', config = {}) => {
    return await apiClient.post('/stage/build', { platform, variant, config });
  },

  getAgentBuilds: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.project) params.append('project', filters.project);

    return await apiClient.get(`/stage/builds?${params.toString()}`);
  },

  getAgentBuild: async (id) => {
    return await apiClient.get(`/stage/builds/${id}`);
  },

  downloadAgentBuildArtifact: async (buildId, artifactFilename) => {
    const response = await apiClient.get(`/stage/builds/${buildId}/download/${artifactFilename}`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', artifactFilename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  },

  deleteAgentBuild: async (id) => {
    return await apiClient.delete(`/stage/builds/${id}`);
  },

  // ============= JOBS =============

  getJobs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.jobType) params.append('jobType', filters.jobType);
    if (filters.beacon) params.append('beacon', filters.beacon);
    if (filters.project) params.append('project', filters.project);
    if (filters.limit) params.append('limit', filters.limit);

    return await apiClient.get(`/jobs?${params.toString()}`);
  },

  getJob: async (id) => {
    return await apiClient.get(`/jobs/${id}`);
  },

  getJobStats: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);

    return await apiClient.get(`/jobs/stats?${params.toString()}`);
  },

  cancelJob: async (id) => {
    return await apiClient.post(`/jobs/${id}/cancel`);
  },

  deleteJob: async (id) => {
    return await apiClient.delete(`/jobs/${id}`);
  },

  getQueueJobs: async (queueName, state = 'waiting', start = 0, end = 50) => {
    return await apiClient.get(`/jobs/queue/${queueName}?state=${state}&start=${start}&end=${end}`);
  },

  cleanQueue: async (queueName, grace = 86400000, status = 'completed') => {
    return await apiClient.post(`/jobs/queue/${queueName}/clean`, { grace, status });
  },

  // ============= SCANS =============

  createPortScan: async (scanData) => {
    return await apiClient.post('/scans/port-scan', scanData);
  },

  getScan: async (jobId) => {
    return await apiClient.get(`/scans/${jobId}`);
  },

  getTimingPresets: async () => {
    return await apiClient.get('/scans/timing-presets');
  },

  getPortPresets: async () => {
    return await apiClient.get('/scans/port-presets');
  },

  // ============= HEALTH =============

  healthCheck: async () => {
    return await apiClient.get('/health');
  },

  // ============= REDIRECTORS =============

  testSSHConnection: async (config) => {
    return await apiClient.post('/redirectors/test-connection', config);
  },

  getRedirectors: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);
    if (filters.isOnline !== undefined) params.append('isOnline', filters.isOnline);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    return await apiClient.get(`/redirectors?${params.toString()}`);
  },

  getRedirector: async (id) => {
    return await apiClient.get(`/redirectors/${id}`);
  },

  createRedirector: async (redirectorData) => {
    return await apiClient.post('/redirectors', redirectorData);
  },

  deployRedirector: async (redirectorData) => {
    return await apiClient.post('/redirectors/deploy', redirectorData);
  },

  updateRedirector: async (id, updates) => {
    return await apiClient.put(`/redirectors/${id}`, updates);
  },

  deleteRedirector: async (id, destroyRemote = false) => {
    return await apiClient.delete(`/redirectors/${id}?destroyRemote=${destroyRemote}`);
  },

  testRedirector: async (id) => {
    return await apiClient.post(`/redirectors/${id}/test`);
  },

  restartRedirector: async (id) => {
    return await apiClient.post(`/redirectors/${id}/restart`);
  },

  getRedirectorLogs: async (id, lines = 100) => {
    return await apiClient.get(`/redirectors/${id}/logs?lines=${lines}`);
  }
};

export default c2Service;
